import { useEffect } from 'react';
import { isTurnedView } from './canvas/viewport';
import { deleteSelection, duplicateSelection, groupSelection, nudgeSelection, ungroupSelection } from './document/actions';
import type { EditorTool, ObjectEditorContextValue } from './EditorContext';
import { t } from '../../potree/translation/markers';

const TOOL_KEYS: Readonly<Record<string, EditorTool>> = { v: 'select', n: 'node', h: 'pan', l: 'line', p: 'polyline', b: 'pen', r: 'rect', c: 'circle', a: 'arc' };

/** One place for every shortcut (the help popover lists the same ones). */
export const EDITOR_SHORTCUTS: ReadonlyArray<{ keys: string; label: string }> = [
  { keys: 'V', label: t('select') },
  { keys: 'N', label: t('edit points (double-click a shape to start)') },
  { keys: 'H / Space', label: t('pan') },
  { keys: 'L', label: t('line') },
  { keys: 'P', label: t('polyline (Enter, right-click or double-click finishes)') },
  { keys: 'B', label: t('pen: click = sharp point, drag = smooth curve') },
  { keys: 'R', label: t('rectangle') },
  { keys: 'C', label: t('circle') },
  { keys: 'A', label: t('arc (center, start, end)') },
  { keys: 'Shift', label: t('45° steps while drawing; add to selection') },
  { keys: 'Ctrl', label: t('hold to place without snapping') },
  { keys: 'Shift / Ctrl', label: t('move or rotate with the gizmo without snapping') },
  { keys: 'Alt', label: t('resize the footprint around its center') },
  { keys: 'T', label: t('toggle test mode') },
  { keys: 'Ctrl+Z / Ctrl+Shift+Z', label: t('undo / redo') },
  { keys: 'Ctrl+D', label: t('duplicate') },
  { keys: 'Ctrl+G / Ctrl+Shift+G', label: t('group / ungroup') },
  { keys: 'Arrows', label: t('nudge (Shift: 10×)') },
  { keys: 'Delete', label: t('delete') },
  { keys: 'Ctrl+S', label: t('save now') },
  { keys: 'Esc', label: t('cancel / deselect') },
];

const isTyping = (e: KeyboardEvent): boolean => e.target instanceof HTMLElement && (e.target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName));

export function useEditorShortcuts({ store, ui, setUi }: Pick<ObjectEditorContextValue, 'store' | 'ui' | 'setUi'>, { onSave, groupName }: { onSave: () => void; groupName: string }): void {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isTyping(e)) return;
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      // Previewing a published version: nothing edits the draft behind it.
      if (ui.preview) {
        if (key === 'escape') setUi((u) => ({ ...u, preview: null }));
        return;
      }

      if (mod && key === 'z') {
        e.preventDefault();
        if (e.shiftKey) store.redo();
        else store.undo();
        return;
      }
      if (mod && key === 'y') {
        e.preventDefault();
        store.redo();
        return;
      }
      if (mod && key === 's') {
        e.preventDefault();
        onSave();
        return;
      }
      if (ui.mode === 'test') {
        if (key === 't' && !mod) setUi((u) => ({ ...u, mode: 'edit' }));
        return;
      }
      if (mod && key === 'd') {
        e.preventDefault();
        duplicateSelection(store);
        return;
      }
      if (mod && key === 'g') {
        e.preventDefault();
        if (e.shiftKey) ungroupSelection(store);
        else groupSelection(store, `${groupName} ${store.getState().spec.groups.length + 1}`);
        return;
      }
      if (mod) return;
      if (key === 'delete' || key === 'backspace') {
        e.preventDefault();
        deleteSelection(store);
        return;
      }
      if (key === 'escape') {
        setUi((u) => ({ ...u, tool: 'select' }));
        store.select({ kind: 'none' });
        return;
      }
      if (key === 't') {
        setUi((u) => ({ ...u, mode: 'test' }));
        return;
      }
      // Arrows move on screen: a turned canvas (wall side on top) runs the world axes the other way.
      const step = (ui.grid || 0.01) * (e.shiftKey ? 10 : 1) * (isTurnedView(store.getState().spec) ? -1 : 1);
      const nudges: Record<string, [number, number]> = { arrowleft: [-step, 0], arrowright: [step, 0], arrowup: [0, step], arrowdown: [0, -step] };
      if (nudges[key]) {
        e.preventDefault();
        nudgeSelection(store, ...nudges[key]);
        return;
      }
      const tool = TOOL_KEYS[key];
      if (tool) setUi((u) => ({ ...u, tool }));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [store, ui.mode, ui.grid, ui.preview, setUi, onSave, groupName]);
}
